import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useInvalidateContextDependentQueries } from '@/hooks/use-invalidate-context-dependent-queries';
import {
  type OrgContextChangedEvent,
  subscribeOrgContextChangedEvents,
} from '@/lib/org-context-cross-tab';
import { resolveOrgContextSyncPath } from '@/lib/org-context-route-sync';
import { useOrgContextStore } from '@/stores/orgContextStore';

export function OrgContextCrossTabDialog() {
  const [pendingEvent, setPendingEvent] = useState<OrgContextChangedEvent | null>(null);
  const setActiveOrg = useOrgContextStore((state) => state.setActiveOrg);
  const invalidateContextQueries = useInvalidateContextDependentQueries();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(
    () =>
      subscribeOrgContextChangedEvents((event) => {
        const current = useOrgContextStore.getState();
        setPendingEvent(
          current.activeOrgId === event.targetOrgId && current.activeContext === event.targetContext
            ? null
            : event
        );
      }),
    []
  );

  const handleSync = () => {
    if (!pendingEvent) return;

    setActiveOrg(pendingEvent.targetOrgId, pendingEvent.targetContext, { broadcast: false });
    invalidateContextQueries();
    const nextPath = resolveOrgContextSyncPath(location.pathname, pendingEvent.targetOrgId);
    if (nextPath && nextPath !== location.pathname) {
      navigate(nextPath, { replace: true });
    }
    setPendingEvent(null);
  };

  return (
    <Dialog open={Boolean(pendingEvent)} onOpenChange={(open) => !open && setPendingEvent(null)}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>其他标签页已切换工作空间</DialogTitle>
          <DialogDescription>
            其他标签页已切换到「{pendingEvent?.targetLabel ?? '新的工作空间'}」。
            你可以保留当前工作空间，继续处理本页内容。
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPendingEvent(null)}>
            保留当前工作空间
          </Button>
          <Button onClick={handleSync}>同步</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
